import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../hooks/useProjects.jsx'
import { useTeam } from '../../hooks/useTeam.js'
import { useFunding } from '../../hooks/useFunding.js'
import { useTranslation } from 'react-i18next'

// Search result categories
const CATEGORIES = {
    PROJECTS: 'projects',
    USERS: 'users',
    TASKS: 'tasks',
    FUNDING: 'funding'
};

// Category icons and colors
const categoryStyles = {
    [CATEGORIES.PROJECTS]: {
        icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
        color: 'text-purple-600',
        bg: 'bg-purple-100',
        label: 'Projects'
    },
    [CATEGORIES.USERS]: {
        icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        label: 'Team'
    },
    [CATEGORIES.TASKS]: {
        icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
        color: 'text-green-600',
        bg: 'bg-green-100',
        label: 'Tasks'
    },
    [CATEGORIES.FUNDING]: {
        icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
        color: 'text-amber-600',
        bg: 'bg-amber-100',
        label: 'Funding'
    }
};

// Search result item component
function SearchResultItem({ item, isSelected, onClick }) {
    const style = categoryStyles[item.category];

    return (
        <button
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
        >
            <div className={`flex-shrink-0 w-9 h-9 rounded-lg ${style.bg} flex items-center justify-center`}>
                <svg className={`w-4 h-4 ${style.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={style.icon} />
                </svg>
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
            </div>
            <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium round ${item.meta === 'Active' || item.meta === 'Completed' || item.meta === 'Approved' ? 'bg-green-100 text-green-700' :
                    item.meta === 'Pending' || item.meta === 'In Progress' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                }`}>
                {item.meta}
            </span>
        </button>
    );
}

// Main SearchModal component
export default function SearchModal({ isOpen, onClose }) {
  const { t } = useTranslation('ui')
    const [query, setQuery] = useState('');
    const { projects } = useProjects();
    const { members: users } = useTeam();
    const { requests: funding } = useFunding();
    const [selectedindex, setSelectedindex] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();

    const lowerQuery = query.toLowerCase();

    const filteredProjects = lowerQuery.length < 2 ? [] : (projects || []).filter(p =>
        p.name?.toLowerCase().includes(lowerQuery) ||
        p.description?.toLowerCase().includes(lowerQuery)
    ).map(p => ({
        id: p.id,
        title: p.name,
        subtitle: p.description?.substring(0, 50) + '...',
        meta: p.status,
        link: `/projects/${p.id}`,
        category: CATEGORIES.PROJECTS
    }));

    const filteredUsers = lowerQuery.length < 2 ? [] : (users || []).filter(u =>
        u.full_name?.toLowerCase().includes(lowerQuery) ||
        u.email?.toLowerCase().includes(lowerQuery) ||
        u.role?.toLowerCase().includes(lowerQuery)
    ).map(u => ({
        id: u.id,
        title: u.full_name,
        subtitle: u.email,
        meta: u.role,
        link: '/team',
        category: CATEGORIES.USERS
    }));

    const filteredFunding = lowerQuery.length < 2 ? [] : (funding || []).filter(f =>
        f.justification?.toLowerCase().includes(lowerQuery) ||
        f.status?.toLowerCase().includes(lowerQuery)
    ).map(f => ({
        id: f.id,
        title: `Funding Request: $${f.amount?.toLocaleString()}`,
        subtitle: f.justification,
        meta: f.status,
        link: '/funding/approval',
        category: CATEGORIES.FUNDING
    }));

    const results = {
        projects: filteredProjects,
        users: filteredUsers,
        tasks: [], // Tasks mapping can be added if needed
        funding: filteredFunding
    };

    // Get flattened results for keyboard navigation
    const allResults = [
        ...results.projects,
        ...results.users,
        ...results.tasks,
        ...results.funding
    ];

    // Filter out categories with no results
    const hasResults = allResults.length > 0;

    // Focus input when modal opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    // Reset selection when query changes
    useEffect(() => {
        setSelectedindex(0);
    }, [query]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedindex(prev => Math.min(prev + 1, allResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedindex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && allResults[selectedindex]) {
            e.preventDefault();
            navigate(allResults[selectedindex].link);
            onClose();
            setQuery('');
        }
    }, [allResults, selectedindex, navigate, onClose]);

    // Handle click on result
    const handleResultClick = (item) => {
        navigate(item.link);
        onClose();
        setQuery('');
    };

    // Handle close
    const handleClose = () => {
        onClose();
        setQuery('');
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-start justify-center p-4 pt-[15vh]">
                <div
                    className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl ring-1 ring-gray-200 overflow-hidden"
                    onKeyDown={handleKeyDown}
                >
                    {/* Search Input */}
                    <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('ph_search_all')}
                            className="flex-1 text-base outline-none placeholder-gray-400"
                        />
                        <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 text-xs text-gray-400 bg-gray-100 round border border-gray-200">
                            ESC
                        </kbd>
                    </div>

                    {/* Results */}
                    <div className="max-h-[400px] overflow-y-auto">
                        {query.length < 2 ? (
                            // Quick actions when no query
                            <div className="p-4">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{t('quick_actions')}</p>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => { navigate('/projects'); handleClose(); }}
                                        className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">{t('view_all_projects')}</span>
                                    </button>
                                    <button
                                        onClick={() => { navigate('/team'); handleClose(); }}
                                        className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">{t('view_team')}</span>
                                    </button>
                                    <button
                                        onClick={() => { navigate('/funding/requests'); handleClose(); }}
                                        className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">{t('funding_requests')}</span>
                                    </button>
                                    <button
                                        onClick={() => { navigate('/finance/reports'); handleClose(); }}
                                        className="flex items-center gap-2 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">{t('financial_reports')}</span>
                                    </button>
                                </div>
                            </div>
                        ) : hasResults ? (
                            // Search results
                            <div className="py-2">
                                {/* Projects */}
                                {results.projects.length > 0 && (
                                    <div>
                                        <p className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50">
                                            Projects ({results.projects.length})
                                        </p>
                                        {results.projects.map((item, idx) => (
                                            <SearchResultItem
                                                key={item.id}
                                                item={item}
                                                isSelected={selectedindex === idx}
                                                onClick={() => handleResultClick(item)}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Users */}
                                {results.users.length > 0 && (
                                    <div>
                                        <p className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50">
                                            Team Members ({results.users.length})
                                        </p>
                                        {results.users.map((item, idx) => (
                                            <SearchResultItem
                                                key={item.id}
                                                item={item}
                                                isSelected={selectedindex === results.projects.length + idx}
                                                onClick={() => handleResultClick(item)}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Tasks */}
                                {results.tasks.length > 0 && (
                                    <div>
                                        <p className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50">
                                            Tasks ({results.tasks.length})
                                        </p>
                                        {results.tasks.map((item, idx) => (
                                            <SearchResultItem
                                                key={item.id}
                                                item={item}
                                                isSelected={selectedindex === results.projects.length + results.users.length + idx}
                                                onClick={() => handleResultClick(item)}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Funding */}
                                {results.funding.length > 0 && (
                                    <div>
                                        <p className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50">
                                            Funding Requests ({results.funding.length})
                                        </p>
                                        {results.funding.map((item, idx) => (
                                            <SearchResultItem
                                                key={item.id}
                                                item={item}
                                                isSelected={selectedindex === results.projects.length + results.users.length + results.tasks.length + idx}
                                                onClick={() => handleResultClick(item)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            // No results
                            <div className="py-12 text-center">
                                <svg className="w-12 h-12 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <p className="mt-2 text-sm text-gray-500">No results found for "{query}"</p>
                                <p className="text-xs text-gray-400">{t('try_searching')}</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                        <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                                <kbd className="px-1.5 py-0.5 bg-white round border border-gray-200">↑</kbd>
                                <kbd className="px-1.5 py-0.5 bg-white round border border-gray-200">↓</kbd>
                                to navigate
                            </span>
                            <span className="flex items-center gap-1">
                                <kbd className="px-1.5 py-0.5 bg-white round border border-gray-200">↵</kbd>
                                to select
                            </span>
                        </div>
                        <span className="hidden sm:block">
                            <kbd className="px-1.5 py-0.5 bg-white round border border-gray-200">Ctrl</kbd> +
                            <kbd className="px-1.5 py-0.5 bg-white round border border-gray-200 ml-1">K</kbd>
                            to open search
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
